import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = getBackendUrl() || '';

const BackupManagement = () => {
  const [driveAuthStatus, setDriveAuthStatus] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);

  // Fetch Drive auth status
  useEffect(() => {
    fetchAuthStatus();
    fetchBackups();
  }, []);

  const fetchAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/auth/drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriveAuthStatus(response.data.authenticated);
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      if (error.response?.status === 401) {
        setMessage({ type: 'error', text: 'Not authorized with Google Drive. Please authenticate first.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticateWithDrive = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/auth/drive/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open Google OAuth in new window
      window.open(response.data.authorization_url, '_blank', 'width=600,height=700');
      
      setMessage({ 
        type: 'info', 
        text: 'Please complete authentication in the popup window, then refresh this page.' 
      });
    } catch (error) {
      console.error('Error initiating Drive auth:', error);
      setMessage({ type: 'error', text: 'Failed to initiate Google Drive authentication' });
    }
  };

  const handleTriggerBackup = async () => {
    try {
      setBackupInProgress(true);
      setMessage({ type: 'info', text: 'Creating backup... This may take a few minutes.' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/backup/trigger`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ 
        type: 'success', 
        text: `Backup completed successfully! ${response.data.files_uploaded.length} files uploaded.` 
      });
      
      // Refresh backups list
      fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to create backup' 
      });
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to restore this backup? This will overwrite existing data.')) {
      return;
    }

    try {
      setRestoreInProgress(true);
      setMessage({ type: 'info', text: 'Restoring backup... Please wait.' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/backup/restore`,
        { backup_id: backupId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ 
        type: 'success', 
        text: `Backup restored successfully! ${Object.keys(response.data.restored_collections).length} collections restored.` 
      });
      
      setSelectedBackup(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to restore backup' 
      });
    } finally {
      setRestoreInProgress(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            📦 Backup Management
          </h1>
          <p className="text-gray-600">
            Manage automated backups to Google Drive. All data and code are backed up weekly.
          </p>
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' :
            message.type === 'error' ? 'bg-red-100 text-red-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            <div className="flex justify-between items-center">
              <span>{message.text}</span>
              <button 
                onClick={() => setMessage(null)}
                className="text-gray-600 hover:text-gray-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Google Drive Auth Status */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Google Drive Connection
          </h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${driveAuthStatus ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-gray-700">
                Status: {driveAuthStatus ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {!driveAuthStatus && (
              <button
                onClick={handleAuthenticateWithDrive}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
              >
                Connect to Google Drive
              </button>
            )}
            {driveAuthStatus && (
              <button
                onClick={fetchAuthStatus}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition"
              >
                Refresh Status
              </button>
            )}
          </div>
        </div>

        {/* Manual Backup Trigger */}
        {driveAuthStatus && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Manual Backup
            </h2>
            <p className="text-gray-600 mb-4">
              Trigger an immediate backup of all data and code to Google Drive.
            </p>
            <button
              onClick={handleTriggerBackup}
              disabled={backupInProgress}
              className={`${
                backupInProgress 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white px-6 py-3 rounded-lg transition flex items-center`}
            >
              {backupInProgress ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Backup...
                </>
              ) : (
                '🚀 Trigger Backup Now'
              )}
            </button>
          </div>
        )}

        {/* Backups List */}
        {driveAuthStatus && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Backup History
              </h2>
              <button
                onClick={fetchBackups}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 mt-4">Loading backups...</p>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No backups found. Create your first backup above.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Backup Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {backups.map((backup) => (
                      <tr key={backup.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{backup.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(backup.created_time).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {backup.web_view_link && (
                            <a
                              href={backup.web_view_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900"
                            >
                              📂 View in Drive
                            </a>
                          )}
                          <button
                            onClick={() => setSelectedBackup(backup.id)}
                            disabled={restoreInProgress}
                            className="text-green-600 hover:text-green-900 ml-4"
                          >
                            ↻ Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Restore Confirmation Modal */}
        {selectedBackup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Confirm Restore
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to restore this backup? This action will overwrite existing data.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setSelectedBackup(null)}
                  disabled={restoreInProgress}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRestoreBackup(selectedBackup)}
                  disabled={restoreInProgress}
                  className={`px-4 py-2 rounded-lg transition ${
                    restoreInProgress
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  } text-white`}
                >
                  {restoreInProgress ? 'Restoring...' : 'Restore Backup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Automatic Backup Info */}
        <div className="bg-blue-50 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            ℹ️ Automated Backup Schedule
          </h3>
          <p className="text-blue-800">
            Automatic backups run every Monday at 2:00 AM. All MongoDB collections and the entire codebase are backed up to your Google Drive folder.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BackupManagement;
